import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState, type FormEvent } from "react";
import { optionalString } from "@/lib/search";
import { AuthFrame } from "@/components/marketing/auth-frame";
import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { authClient } from "@/lib/host/auth-client";

export const Route = createFileRoute("/reset-password")({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: optionalString(search.token),
  }),
  component: ResetPasswordPage,
});

/** Takes a new password, given the token from the emailed link. */
function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  /* Which field the error is about, so only that one is marked and focused. */
  const [invalid, setInvalid] = useState<"password" | "confirm" | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setInvalid(null);
    if (password.length < 8) {
      setError("Use at least 8 characters.");
      setInvalid("password");
      passwordRef.current?.focus();
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match. Type the new password again.");
      setInvalid("confirm");
      confirmRef.current?.focus();
      return;
    }
    if (!token) {
      setError("This link is missing its token. Ask for a new one.");
      return;
    }
    setPending(true);
    let result: Awaited<ReturnType<typeof authClient.resetPassword>>;
    try {
      result = await authClient.resetPassword({ newPassword: password, token });
    } catch {
      setError("Couldn't reach the server. Check your connection and try again.");
      return;
    } finally {
      setPending(false);
    }
    if (result.error) {
      setError(
        result.error.code === "INVALID_TOKEN" || !result.error.message
          ? "This link has expired or was already used. Ask for a new one from the sign-in page."
          : result.error.message,
      );
      return;
    }
    await navigate({ to: "/login", search: { reason: "password-reset" } });
  };

  const describe = (field: "password" | "confirm", hint?: string) => {
    const ids = [hint, invalid === field ? "reset-error" : undefined].filter(Boolean).join(" ");
    return {
      "aria-invalid": invalid === field ? true : undefined,
      "aria-describedby": ids || undefined,
    };
  };

  return (
    <AuthFrame>
      <CardHeader className="items-center text-center">
        <CardTitle asChild className="text-xl">
          <h1>{token ? "Choose a new password" : "This reset link is incomplete"}</h1>
        </CardTitle>
        <CardDescription>
          {token
            ? "You will sign in with this from now on."
            : "It was probably cut off when it was copied. Ask for a new link and open it straight from the email."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {token ? (
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <Input
                ref={passwordRef}
                autoComplete="new-password"
                id="password"
                required
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                {...describe("password", "password-hint")}
              />
              <p id="password-hint" className="text-muted-foreground text-xs">
                At least 8 characters.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">New password again</Label>
              <Input
                ref={confirmRef}
                autoComplete="new-password"
                id="confirm"
                required
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                {...describe("confirm")}
              />
            </div>
            <p id="reset-error" role="alert" className="text-destructive text-sm empty:hidden">
              {error}
            </p>
            <Button className="w-full" disabled={pending} type="submit">
              {pending ? <Spinner /> : null}
              {pending ? "Saving…" : "Save new password"}
            </Button>
          </form>
        ) : (
          <div className="space-y-2">
            <Button asChild className="w-full">
              <Link to="/forgot-password">Send a new link</Link>
            </Button>
            <Button asChild className="w-full" variant="ghost">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </AuthFrame>
  );
}
